/**
 * useFileDrop — drag-and-drop router for the app window.
 *
 * Attaches document-level drag/drop listeners, resolves each dropped File's
 * native path, and delegates routing to `routeDroppedFiles` (the pure
 * `decideDropAction` classifier + side effects): `.sql` files open in the
 * script editor, data files (csv/tsv/txt/json/ndjson/xlsx) open the Data
 * Import wizard — first import file wins on multi-file drops, the rest are
 * ignored with a toast.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { isBrowserMode } from '../../browser-mode';
import { classifyDragPayload } from '../fileDropClassifier';
import { fileDropTargetAt, routeDroppedFiles } from '../fileDropRouter';

/**
 * Resolve the native file path for a dropped File object.
 * Uses Electron's webUtils.getPathForFile() (Electron 32+ replacement for File.path).
 */
function getFilePath(file: File): string {
  // Electron 32+: use webUtils.getPathForFile() exposed via preload
  if (window.electronAPI?.getPathForFile) {
    return window.electronAPI.getPathForFile(file);
  }
  // Legacy fallback: File.path (removed in Electron 32)
  return (file as File & { path?: string }).path ?? '';
}

export function useFileDrop() {
  const [isDragging, setIsDragging] = useState(false);
  const [dragKind, setDragKind] = useState<'import' | 'default' | 'upload'>('default');
  const [uploadLabel, setUploadLabel] = useState<string>();
  const dragCounter = useRef(0);

  const handleDragEnter = useCallback((e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current += 1;
    if (dragCounter.current === 1) {
      // Only show overlay if the drag contains files
      if (e.dataTransfer?.types.includes('Files')) {
        setIsDragging(true);
        const target = fileDropTargetAt(e.target);
        setDragKind(target ? 'upload' : classifyDragPayload(Array.from(e.dataTransfer?.types ?? [])));
        setUploadLabel(target?.label);
      }
    }
  }, []);

  const handleDragLeave = useCallback((e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current -= 1;
    if (dragCounter.current <= 0) {
      dragCounter.current = 0;
      setIsDragging(false);
      setDragKind('default');
      setUploadLabel(undefined);
    }
  }, []);

  const handleDragOver = useCallback((e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    // Only a drag carrying files may show the upload overlay (review E F7), like `handleDragEnter`.
    const target = e.dataTransfer?.types.includes('Files') ? fileDropTargetAt(e.target) : undefined;
    if (target) {
      setIsDragging(true);
      setDragKind('upload');
      setUploadLabel(target.label);
    }
  }, []);

  const handleDrop = useCallback(async (e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current = 0;
    setIsDragging(false);
    setDragKind('default');
    setUploadLabel(undefined);

    const files = e.dataTransfer?.files;
    if (!files || files.length === 0) return;

    const entries = Array.from(files).map((file) => ({ name: file.name, path: getFilePath(file) }));
    const target = fileDropTargetAt(e.target);
    if (target) {
      await target.onDrop(entries);
      return;
    }
    await routeDroppedFiles(entries);
  }, []);

  useEffect(() => {
    // Skip in browser mode — no native file paths available
    if (isBrowserMode()) return;

    document.addEventListener('dragenter', handleDragEnter);
    document.addEventListener('dragleave', handleDragLeave);
    document.addEventListener('dragover', handleDragOver);
    document.addEventListener('drop', handleDrop);

    return () => {
      document.removeEventListener('dragenter', handleDragEnter);
      document.removeEventListener('dragleave', handleDragLeave);
      document.removeEventListener('dragover', handleDragOver);
      document.removeEventListener('drop', handleDrop);
    };
  }, [handleDragEnter, handleDragLeave, handleDragOver, handleDrop]);

  return { isDragging, dragKind, uploadLabel };
}
